//
// neo.ts — near-Earth objects
//
// Translation of `Networking/NEOService.swift`.
//
// One honest caveat, carried over from the Swift and worth restating
// because the map makes it look otherwise: **NeoWs does not give these
// objects a position over the Earth.** It gives approach dates, diameters
// and miss distances. The latitudes and longitudes below are derived from
// the list index — decorative placement, not astronomy. They are on the map
// because the original Fuser app put them there, and they are flagged here
// so nobody later mistakes them for tracked positions the way the flight
// data was mistaken for live before this build.
//
// The `DEMO_KEY` this uses is shared by the entire internet and rate-limits
// hard, so failures are routine — hence the named fallback catalogue.
//

import type { TrackedObject } from "../types";
import { NASA_API_KEY, NASA_NEO_FEED, fetchJSON } from "./config";

interface RawResponse {
  near_earth_objects?: Record<string, Array<{
    id: string;
    name: string;
    is_potentially_hazardous_asteroid: boolean;
  }>>;
}

export async function fetchNEOs(date: Date = new Date()): Promise<TrackedObject[]> {
  const dateString = date.toISOString().slice(0, 10);
  const url =
    `${NASA_NEO_FEED}?start_date=${dateString}&end_date=${dateString}` +
    `&api_key=${NASA_API_KEY}`;

  const response = await fetchJSON<RawResponse>(url);
  const rawList = Object.values(response?.near_earth_objects ?? {}).flat();

  if (rawList.length === 0) return fallbackCatalog();

  return rawList.slice(0, 10).map((neo, idx) => ({
    id: `neo-${neo.id}`,
    name: neo.name,
    category: neo.is_potentially_hazardous_asteroid
      ? ("neo_hazardous" as const)
      : ("neo_safe" as const),
    typeLabel: neo.is_potentially_hazardous_asteroid
      ? "HAZARDOUS ASTEROID"
      : "NEAR EARTH OBJECT",
    // Decorative placement — see the file header.
    layer: "satLuminaries" as const,
    latitude: Math.cos(idx * 1.2) * 65,
    longitude: ((idx * 36 - 100) % 360),
    altitudeKm: 120000 + idx * 45000,
    velocityKms: 18.4 + idx * 1.2,
    orbitalPeriodMin: 520,
    inclinationDeg: 24.5,
    dataSource: "NASA JPL Center for NEO Studies",
    lastUpdated: "Just now",
  }));
}

/** Named, well-known NEOs — used when the live call fails. */
export function fallbackCatalog(): TrackedObject[] {
  return [
    {
      id: "neo-99942", name: "99942 APOPHIS (2004 MN4)",
      category: "neo_hazardous", typeLabel: "POTENTIALLY HAZARDOUS ASTEROID",
      layer: "satLuminaries" as const,
      latitude: 14.2, longitude: -68.4,
      altitudeKm: 31600, velocityKms: 30.73,
      orbitalPeriodMin: 4600, inclinationDeg: 3.33,
      dataSource: "NASA JPL Horizons", lastUpdated: "Live",
      diameterMeters: 370, missDistanceKm: 31600,
    },
    {
      id: "neo-101955", name: "101955 BENNU (1999 RQ36)",
      category: "neo_hazardous", typeLabel: "CARBONACEOUS NEAR-EARTH ASTEROID",
      layer: "satLuminaries" as const,
      latitude: -28.1, longitude: 142.5,
      altitudeKm: 185000, velocityKms: 27.9,
      orbitalPeriodMin: 6200, inclinationDeg: 6.03,
      dataSource: "NASA OSIRIS-REx Team", lastUpdated: "Live",
      diameterMeters: 490, missDistanceKm: 185000,
    },
    {
      id: "neo-433", name: "433 EROS (1898 DQ)",
      category: "neo_safe", typeLabel: "S-TYPE NEAR-EARTH ASTEROID",
      layer: "satLuminaries" as const,
      latitude: 52.1, longitude: -12.4,
      altitudeKm: 22000000, velocityKms: 24.3,
      orbitalPeriodMin: 26000, inclinationDeg: 10.8,
      dataSource: "NASA JPL CNEOS", lastUpdated: "Live",
      diameterMeters: 16840, missDistanceKm: 22000000,
    },
  ];
}
